import { classNames } from "primereact/utils";
import { memo, useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { dateFieldValidations } from "./formUtils";
import { onShowCalendarOverlay } from "../../utils/UtilFunctions";
import useCalendarDateEvents from "../common-hooks/useCalendarDateEvents";

const isInvalidDate = (value) => value && !(value instanceof Date);

const getCalendarValue = (
  value,
  isShowHideError = false,
  isEmptyStringError = false
) => {
  if (isEmptyStringError) {
    return new Date();
  }
  return isInvalidDate(value) && !isShowHideError ? new Date() : value;
};

const FormCalendarDate = ({
  name,
  value,
  errors,
  clearErrors,
  getDate,
  setDate,
  getChangedDateValues,
  options,
  dropdownStyle,
  updatedOptions,
  setError,
  fieldTabIndex,
  calendarBtnTabIndex,
}) => {
  const inputRef = useRef();
  const { onBlur, onFocus, onKeyDown, setCaretPosition, onHide } =
    useCalendarDateEvents(value, inputRef);
  let calendarSelectButton = null;
  let calendarInput = null;
  let calendarContainerRef = null;
  const [isOverlayHidden, setIsOverlayHidden] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [isShowHideError, setIsShowHideError] = useState(false);
  const [isEmptyStringError, setIsEmptyStringError] = useState(false);

  useEffect(() => {
    if (isOverlayHidden && calendarSelectButton) {
      calendarSelectButton.focus();
      setIsOverlayHidden(false);
    }
  }, [isOverlayHidden, value]);

  useEffect(() => {
    if (isInvalidDate(getDate(value))) {
      inputRef.current.value = getDate(value);
    }
  }, [Object.keys(errors).length, value]);

  useEffect(() => {
    if (isEmptyStringError) {
      inputRef.current.value = "";
    }
  }, [isEmptyStringError]);

  return (
    <>
      <Calendar
        id={name}
        inputRef={inputRef}
        inputId={name}
        showButtonBar
        name={name}
        key={forceUpdate}
        value={getCalendarValue(
          getDate(value),
          isShowHideError,
          isEmptyStringError
        )}
        dateFormat="mm/dd/yy"
        // readOnlyInput
        className={classNames({
          "p-invalid": errors[name],
        })}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          clearErrors(name);
          const value = e.value === "//" ? "" : e.value;
          setDate(name, value);
          getChangedDateValues?.(name, value, calendarInput);
          if (!value) {
            inputRef.current.value = "//";
            setCaretPosition(inputRef.current, 0, 0);
          }
          if (isInvalidDate(value)) {
            inputRef.current.value = value;
            setIsShowHideError(false);
          }
        }}
        showIcon
        showOnFocus={false}
        keepInvalid
        onBlur={(e) => {
          getChangedDateValues?.(name, value, calendarInput);
          dateFieldValidations(value, updatedOptions?.required?.value, {
            isBlur: true,
            setError,
            clearErrors,
            fieldName: name,
            hasPreviousErrors: Object.keys(errors).length > 0,
          });
          if (isInvalidDate(getDate(value))) {
            setIsShowHideError(true);
          }
          onBlur(e);
        }}
        onHide={() => {
          calendarSelectButton?.focus();
          setForceUpdate((prev) => !prev);
          setIsOverlayHidden(true);
          if (isInvalidDate(getDate(value))) {
            setIsShowHideError(true);
          }
          if (isEmptyStringError) {
            setIsEmptyStringError(false);
          }
          onHide(inputRef.current);
        }}
        ref={(calendarRef) => {
          if (calendarRef) {
            calendarContainerRef = calendarRef;
            const calendarBtn = calendarRef?.getElement()?.lastChild;
            calendarInput = calendarRef?.getInput();
            calendarSelectButton = calendarBtn;
            calendarBtn.tabIndex = calendarBtnTabIndex || 0;
          }
        }}
        onShow={() => {
          onShowCalendarOverlay(getDate(value), calendarContainerRef);
          if (!value) {
            setIsEmptyStringError(true);
          }
        }}
        onFocus={onFocus}
        style={dropdownStyle}
        {...(fieldTabIndex && { tabIndex: fieldTabIndex })}
      />
      {options["required"] && <sup className="star-label">*</sup>}
    </>
  );
};

export default memo(FormCalendarDate);